import { useMutation } from '@apollo/client';
import { Application, MutationResponse } from '@bybeam/platform-types';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import useErrorIndicator from '@platform-ui-common/hooks/useErrorIndicator.js';
import MutationHookResponse from '@platform-ui/@types/mutation.js';
import SubmitApplicationMutation from '@platform-ui/common/graphql/mutations/SubmitApplication.graphql';
import { useCallback, useMemo } from 'react';
import ApplicantRoutes from '../utils/ApplicantRoutes.js';
import usePushApplicationPage from './usePushApplicationPage.js';

export default function useSubmitApplication(): MutationHookResponse<void> {
  const [submitApplication, { data, loading, error: transportError }] = useMutation<{
    application: { submit: MutationResponse<Application> };
  }>(SubmitApplicationMutation);

  const showError = useErrorIndicator();
  const pushApplicationPage = usePushApplicationPage();
  const application = useApplication();

  // biome-ignore lint/correctness/useExhaustiveDependencies: wants doMutation itself as a dep
  const doMutation = useCallback(async () => {
    // TODO: Should we do validation on the frontend? We shouldn't let users go to this page
    // if they haven't completed all of the answers, but we aren't blocking the route or anything.
    // This will be a lot easier once we have application configuration as code/JSON, so maybe
    // wait to address until then.
    const {
      data: {
        application: {
          submit: { metadata },
        },
      },
    } = await submitApplication({
      variables: { input: { id: application.id } },
    });

    if (metadata.status < 400) {
      pushApplicationPage(ApplicantRoutes.SUBMISSION_CONFIRMATION);
      return true;
    }

    showError('There was an error submitting your application.', doMutation);
    return false;
  }, [application, submitApplication, pushApplicationPage, showError]);

  const error = transportError
    ? 'Something went wrong!'
    : data?.application.submit.metadata.errors?.[0];

  return useMemo(() => ({ doMutation, loading, error }), [doMutation, loading, error]);
}
